import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  MediaFilesIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { API_MAX_LIMIT } from '@/constants';
import { useGetPrograms } from '@/features/programs/api/get-programs';
import { useProgramsByParams } from '@/features/programs/api/get-programs-by-params';
import { TProgram } from '@/features/programs/types';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

import { TRangeActiveOptions } from '../active-options/active-options';
import {
  CatalogueActiveOptions,
  TUseCatalogueOptions,
} from '../active-options/CatalogueActiveOptions';

const toProgramItem = (program: TProgram) => ({
  value: program.id,
  label: program.name,
});

/** Sends no programme state: the list shows the segments of inactive programmes too. */
const useProgramOptions: TUseCatalogueOptions<TProgram> = (request, config) =>
  useProgramsByParams({
    params:
      'range' in request
        ? { ...request.range, limit: API_MAX_LIMIT }
        : { idIn: request.idIn, limit: request.idIn.length },
    config,
  });

export function MentionFilterPrograms({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  activeOptions,
}: TFilterProps & { activeOptions?: TRangeActiveOptions }) {
  const { t } = useTranslation();
  const { programs: state, setFilters: setState } = useProgramBroadcastFilters();
  const { data: { data: programsData = [] } = {} } = useGetPrograms({
    config: { enabled: !activeOptions },
  });

  const allPrograms = useMemo(() => {
    return programsData.map(state => ({
      value: state.id,
      label: state.name,
    }));
  }, [programsData]);

  const handleChangeState = (value: string[]) => {
    setState({ programs: value });
  };

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Programas')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && allPrograms.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <MediaFilesIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Programas')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          {activeOptions ? (
            <CatalogueActiveOptions
              activeOptions={activeOptions}
              useOptions={useProgramOptions}
              toItem={toProgramItem}
              isDark={isDark}
              selected={state}
              onChange={handleChangeState}
              onClose={onClose}
            />
          ) : (
            <Listbox
              dark={isDark}
              mode="multiple"
              items={allPrograms}
              onChange={value => {
                handleChangeState(value);
              }}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
                children: 'options',
              }}
              selected={state}
              onClose={onClose}
            />
          )}
        </div>
      )}
    </div>
  );
}
